import { SimpleGrid, useDisclosure, useToast } from "@chakra-ui/react";
import { useFormik } from "formik";
import * as Yup from "yup";
import { get } from "lodash";
import React, { useEffect } from "react";
import { useParams } from "react-router-dom";
import { PROGRAM, PROGRAM_CATEGORY, STUDENT } from "../../../../consts/methods.consts";
import { DROPDOWN_TAGS, PROGRAM_CATEGORY_TAGS, PROGRAM_TAGS, STUDENT_TAG } from "../../../../consts/tags.consts";
import { useGetQuery, usePostMutation } from "../../../../services/api.service";
import ChakraTable, { ChakraTableColumns } from "../../../common/chakra-table/ChakraTable";
import { PageHeaderActions } from "../../../common/page-header/PageHeader";
import { SectionCard } from "../../../common/section-card/SectionCard";
import { DefaultCell, DefaultCellV2 } from "../../report/components/cells";
import { ToastService } from "../../../../services/toast.service";
import { customeJsonParser } from "../../../../utils/utils";
import ChakraModal from "../../../common/chakra-modal/ChakraModal";
import { FormInput } from "../../../common/form-controllers/FormInput";
import FormOnlineSearch from "../../../common/form-controllers/FormOnlineSearch";
import { FormTextArea } from "../../../common/form-controllers/FormTextArea";

const ProgramModuleExtensionTab = () => {
    const { id } = useParams()
    const disclosure = useDisclosure()
    const editDisclosure = useDisclosure()
    const toast = new ToastService(useToast())
    const [create,{isLoading:creating}] = usePostMutation();

    const { data: _data, isLoading, isFetching } = useGetQuery({
        method: STUDENT.GET_PROGRAM_MODULE_EXTENSION_LIST,
        body: { id },
        providesTags: [STUDENT_TAG.EXTENSION_LIST]
    })

    const formik = useFormik({
        initialValues: {

        },
        validationSchema: Yup.object({
            original_program: Yup.string().required("Original Program is required"),
            original_program_module: Yup.string().required("Original Program Module is required"),
            extension_program: Yup.string().required("Extension Program is required"),
            extension_program_module: Yup.string().required("Extension Program Module is required"),
        }),
        onSubmit: async (values) => {
            try {
                await create({
                    method: STUDENT.CREATE_PROGRAM_EXTENSION,
                    body: { ...values, student: id },
                    invalidatesTags: [STUDENT_TAG.EXTENSION_LIST, DROPDOWN_TAGS.LIST]
                }).unwrap();

                toast.setTitle("Success").setDescription("Program module extension has been created successfully").showSuccessToast();

            } catch (error: any) {
                let message = customeJsonParser(get(error?.data, '_server_messages', {}))
                if (message) toast.setTitle("Error").setDescription(customeJsonParser(message)?.message).showErrorToast();
                console.error(error);
            } finally {
                disclosure.onClose();
            }
        }
    })

    const editFormik = useFormik({
        initialValues: {

        },
        validationSchema: Yup.object({
            original_program: Yup.string().required("Original Program is required"),
            original_program_module: Yup.string().required("Original Program Module is required"),
            extension_program: Yup.string().required("Extension Program is required"),
            extension_program_module: Yup.string().required("Extension Program Module is required"),
        }),
        onSubmit: async (values) => {
            try {
                await create({
                    method: STUDENT.UPDATE_PROGRAM_EXTENSION,
                    body: values,
                    invalidatesTags: [STUDENT_TAG.EXTENSION_LIST, DROPDOWN_TAGS.LIST]
                }).unwrap();

                toast.setTitle("Success").setDescription("Program module extension has been updated successfully").showSuccessToast();

            } catch (error: any) {
                let message = customeJsonParser(get(error?.data, '_server_messages', {}))
                if (message) toast.setTitle("Error").setDescription(customeJsonParser(message)?.message).showErrorToast();
                console.error(error);
            } finally {
                editDisclosure.onClose();
            }
        }
    })

    const onDeleteHandler = async (item: any) => {
        try {
            await create({
                method: STUDENT.DELETE_PROGRAM_EXTENSION,
                body: { id: get(item, 'name') },
                invalidatesTags: [STUDENT_TAG.EXTENSION_LIST, DROPDOWN_TAGS.LIST]
            }).unwrap();

            toast.setTitle("Success").setDescription("Program module extension has been deleted successfully").showSuccessToast();

        } catch (error: any) {
            let message = customeJsonParser(get(error?.data, '_server_messages', {}))
            if (message) toast.setTitle("Error").setDescription(customeJsonParser(message)?.message).showErrorToast();
            console.error(error);
        }
    }


    const data = get(_data, 'message', [])

    const originalProgram = get(formik, 'values.original_program')
    const extension_program = get(formik, 'values.extension_program')

    const editOriginalProgram = get(editFormik, 'values.original_program')
    const editExtensionProgram = get(editFormik, 'values.extension_program')


    useEffect(() => {
        if (disclosure.isOpen) {
            formik.resetForm();
        }
    }, [disclosure.isOpen])

    useEffect(() => {
        formik.setFieldValue("original_program_module", "")
    }, [originalProgram])

    useEffect(() => {
        formik.setFieldValue("extension_program_module", "")
    }, [extension_program])

    useEffect(() => {
        if (editDisclosure.isOpen) {
            formik.setFieldValue("original_program_module", "")
        }
    }, [editOriginalProgram])

    useEffect(() => {
        if (editDisclosure.isOpen) {
            formik.setFieldValue("extension_program", "")
        }
    }, [editExtensionProgram])


    const MODULE_ENROLLMENTS_COLUMNS: ChakraTableColumns = [
        {
            header: "Original Program",
            width: 200,
            accessor: "_original_program",
            customRenderer: (value: any, item: any) => (
                React.createElement(DefaultCell, { navigateTo: `/app/program/${get(item, 'original_program')}`, value: value })
            ),
        },
        {
            header: "Original Program Module",
            width: 200,
            accessor: "_original_program_module",
            customRenderer: (value: any, item: any) => (
                React.createElement(DefaultCell, { navigateTo: `/app/program-module/${get(item, 'original_program_module')}`, value: value })
            ),
        },
        {
            header: "Extension Program",
            width: 200,
            accessor: "_extension_program",
            customRenderer: (value: any, item: any) => (
                React.createElement(DefaultCell, { navigateTo: `/app/program/${get(item, 'extension_program')}`, value: value })
            ),
        },
        {
            header: "Extension Program Module",
            width: 200,
            accessor: "_extension_program_module",
            customRenderer: (value: any, item: any) => (
                React.createElement(DefaultCell, { navigateTo: `/app/program-module/${get(item, 'extension_program_module')}`, value: value })
            ),
        },
        {
            header: "Extension Reason",
            width: 100,
            accessor: "extension_reason",
        },
        {
            header: "Actions",
            accessor: '',
            disabled: false,
            width: 100,
            options: [
                {
                    label: "Edit",
                    confirmation: false,
                    onClick(item) {
                        editFormik.resetForm();
                        editFormik.setFieldValue("name", get(item, "name"))
                        editFormik.setFieldValue("original_program", get(item, "original_program"))
                        editFormik.setFieldValue("original_program_module", get(item, "original_program_module"))
                        editFormik.setFieldValue("extension_program", get(item, "extension_program"))
                        editFormik.setFieldValue("extension_program_module", get(item, "extension_program_module"))
                        editFormik.setFieldValue("extension_reason", get(item, "extension_reason"))
                        editDisclosure.onOpen()
                    },
                },
                {
                    label: "Delete",
                    confirmation: true,
                    onClick: onDeleteHandler,
                }
            ]
        }
    ]

    const actions: PageHeaderActions = [
        {
            text: "New",
            buttonVariant: "solid",
            buttonColorScheme: "teal",
            onClick: disclosure.onOpen,
            confirmation: false
        }
    ]

    return (

        <div>
            <SectionCard actions={actions} backgroundColor=" bg-white" title="Program Module Extensions" p={3}>
                <ChakraTable title={''} size={"sm"} isLoading={isLoading || isFetching} isFetching={isFetching} 
                    columns={MODULE_ENROLLMENTS_COLUMNS} data={data} />
            </SectionCard>
            <ChakraModal size="3xl" primaryAction={formik.submitForm} primaryButtonText={"Save"} modalTitle="New Program Module Extensions" {...disclosure}>
                <SimpleGrid columns={{ base: 1, md: 2, lg: 2, xl: 2 }} spacing={3}>
                    <FormOnlineSearch
                        method={STUDENT.GET_PROGRAM_LIST_DD}
                        body={{ id }}
                        isRequired
                        formik={formik}
                        name="original_program"
                        label="Original Program" />

                    <FormOnlineSearch
                        method={STUDENT.GET_PROGRAM_MODULES_DD}
                        body={{ id, program: originalProgram }}
                        isDisabled={!originalProgram}
                        isRequired formik={formik}
                        name="original_program_module"
                        label="Original Program Module" />

                    <FormOnlineSearch
                        method={STUDENT.GET_PROGRAM_LIST_DD}
                        body={{ id }}
                        isRequired
                        formik={formik}
                        name="extension_program"
                        label="Extension Program" />

                    <FormOnlineSearch
                        method={STUDENT.GET_PROGRAM_MODULES_DD}
                        body={{ id, program: extension_program }}
                        isDisabled={!extension_program}
                        isRequired
                        formik={formik}
                        name="extension_program_module"
                        label="Extension Program Module" />

                    <FormTextArea formik={formik} name="extension_reason" label="Extension Reason" />
                </SimpleGrid>
            </ChakraModal>
            <ChakraModal size="3xl" disablePrimaryButton={creating} primaryAction={editFormik.submitForm} primaryButtonText={"Save"} modalTitle="Edit Program Module Extensions" {...editDisclosure}>
                <SimpleGrid columns={{ base: 1, md: 2, lg: 2, xl: 2 }} spacing={3}>
                    <FormOnlineSearch
                        method={STUDENT.GET_PROGRAM_LIST_DD}
                        body={{ id }}
                        isRequired
                        formik={editFormik}
                        name="original_program"
                        label="Original Program" />

                    <FormOnlineSearch
                        method={STUDENT.GET_PROGRAM_MODULES_DD}
                        body={{ id, program: editOriginalProgram }}
                        isDisabled={!editOriginalProgram}
                        isRequired formik={editFormik}
                        name="original_program_module"
                        label="Original Program Module" />

                    <FormOnlineSearch
                        method={STUDENT.GET_PROGRAM_LIST_DD}
                         body={{ id }}
                        isRequired
                        formik={editFormik}
                        name="extension_program"
                        label="Extension Program" />

                    <FormOnlineSearch
                        method={STUDENT.GET_PROGRAM_MODULES_DD}
                        body={{ id, program: editExtensionProgram }}
                        isDisabled={!editExtensionProgram}
                        isRequired
                        formik={editFormik}
                        name="extension_program_module"
                        label="Extension Program Module" />

                    <FormTextArea formik={editFormik} name="extension_reason" label="Extension Reason" />
                </SimpleGrid>
            </ChakraModal>
        </div>
    )
}

export default ProgramModuleExtensionTab;